// src/components/ManageTeamsModal.tsx
import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Star, AlertTriangle } from 'lucide-react';
import { getCookie } from '../utils/utils';

interface Team {
  id: number;
  name: string;
  org: number;
  is_default: boolean;
  member_count: number;
}

interface ManageTeamsModalProps {
  isOpen: boolean;
  orgId: number;
  onClose: () => void;
  onTeamsChanged: () => void;
}

const ManageTeamsModal: React.FC<ManageTeamsModalProps> = ({
  isOpen,
  orgId,
  onClose,
  onTeamsChanged,
}) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [deletingTeam, setDeletingTeam] = useState<Team | null>(null);
  const [replacementTeamId, setReplacementTeamId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTeams();
    }
  }, [isOpen, orgId]);

  const fetchTeams = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/teams/?org_id=${orgId}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      } else {
        setError('Failed to load teams');
      }
    } catch (err) {
      setError('An error occurred while loading teams');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    setIsCreating(true);
    setError(null);
    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch('/api/teams/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
        body: JSON.stringify({ org: orgId, name: newTeamName.trim() }),
      });
      if (response.ok) {
        setNewTeamName('');
        await fetchTeams();
        onTeamsChanged();
      } else {
        const data = await response.json();
        setError(data?.error || 'Failed to create team');
      }
    } catch (err) {
      setError('An error occurred while creating the team');
    } finally {
      setIsCreating(false);
    }
  };

  const handleRenameTeam = async (teamId: number) => {
    if (!editName.trim()) return;
    setError(null);
    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch(`/api/teams/${teamId}/`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
        body: JSON.stringify({ name: editName.trim() }),
      });
      if (response.ok) {
        setEditingTeamId(null);
        setEditName('');
        await fetchTeams();
        onTeamsChanged();
      } else {
        const data = await response.json();
        setError(data?.error || 'Failed to rename team');
      }
    } catch (err) {
      setError('An error occurred while renaming the team');
    }
  };

  const handleSetDefault = async (teamId: number) => {
    setError(null);
    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch(`/api/teams/${teamId}/set_default/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
      });
      if (response.ok) {
        await fetchTeams();
        onTeamsChanged();
      } else {
        const data = await response.json();
        setError(data?.error || 'Failed to set default team');
      }
    } catch (err) {
      setError('An error occurred');
    }
  };

  const handleDeleteTeam = async () => {
    if (!deletingTeam || !replacementTeamId) return;
    setIsDeleting(true);
    setError(null);
    try {
      const csrfToken = getCookie('csrftoken');
      const response = await fetch(`/api/teams/${deletingTeam.id}/?replacement_team_id=${replacementTeamId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
        body: JSON.stringify({ replacement_team_id: replacementTeamId }),
      });
      if (response.ok) {
        setDeletingTeam(null);
        setReplacementTeamId(null);
        await fetchTeams();
        onTeamsChanged();
      } else {
        const data = await response.json();
        setError(data?.error || 'Failed to delete team');
      }
    } catch (err) {
      setError('An error occurred while deleting the team');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Manage Teams</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Create New Team */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Create New Team</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateTeam()}
                placeholder="Team name..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                disabled={isCreating}
              />
              <button
                onClick={handleCreateTeam}
                disabled={isCreating || !newTeamName.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                {isCreating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>

          {/* Teams List */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Teams</label>
            {isLoading ? (
              <p className="text-sm text-gray-500">Loading teams...</p>
            ) : teams.length === 0 ? (
              <p className="text-sm text-gray-500">No teams found.</p>
            ) : (
              teams.map((team) => (
                <div
                  key={team.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {editingTeamId === team.id ? (
                      <div className="flex gap-2 flex-1">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRenameTeam(team.id);
                            if (e.key === 'Escape') { setEditingTeamId(null); setEditName(''); }
                          }}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-indigo-500"
                          autoFocus
                        />
                        <button
                          onClick={() => handleRenameTeam(team.id)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => { setEditingTeamId(null); setEditName(''); }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => { setEditingTeamId(team.id); setEditName(team.name); }}
                          className="text-sm font-medium text-gray-900 hover:text-indigo-600 truncate text-left"
                          title="Click to rename"
                        >
                          {team.name}
                        </button>
                        {team.is_default && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                            <Star className="w-3 h-3" />
                            Default
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {team.member_count} member{team.member_count !== 1 ? 's' : ''}
                        </span>
                      </>
                    )}
                  </div>

                  {editingTeamId !== team.id && (
                    <div className="flex items-center gap-1 ml-2">
                      {!team.is_default && (
                        <button
                          onClick={() => handleSetDefault(team.id)}
                          className="p-1 rounded text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 transition-colors"
                          title="Set as default team"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      {teams.length > 1 && (
                        <button
                          onClick={() => { setDeletingTeam(team); setReplacementTeamId(null); }}
                          className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete team"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Delete Confirmation */}
          {deletingTeam && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h4 className="text-sm font-semibold text-red-800">
                  Delete "{deletingTeam.name}"?
                </h4>
              </div>
              <p className="text-sm text-red-700 mb-3">
                All members and projects will be moved to the replacement team.
              </p>
              <div className="mb-3">
                <label className="block text-xs font-medium text-red-700 mb-1">
                  Move members & projects to:
                </label>
                <select
                  value={replacementTeamId ?? ''}
                  onChange={(e) => setReplacementTeamId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-red-300 rounded-md text-sm focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Select a team...</option>
                  {teams
                    .filter((t) => t.id !== deletingTeam.id)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { setDeletingTeam(null); setReplacementTeamId(null); }}
                  disabled={isDeleting}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTeam}
                  disabled={isDeleting || !replacementTeamId}
                  className="flex-1 px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Delete & Move'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageTeamsModal;
